import React, { useState, useContext } from 'react';
import { Link as ReactRouterLink, useHistory } from 'react-router-dom';
import { QueryClient } from 'react-query';
import { AppBar } from '@fortress-technology-solutions/fortress-component-library/Organisms_Portal';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  IconButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  HomeIcon,
  LogoutIcon,
  CloseIcon,
  ContractIcon,
  ArrowBackIcon,
  MenuIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { UserContext } from 'contexts/UserContext';
import { clearUserMetadata } from 'utils/datadog';
import { AppContext } from 'contexts/AppContext';
import { logoutFusionAuthUser } from 'services/fusionAuthService';
import { destroyLocalAuth } from 'utils/auth';
import { useGoBack } from 'components/Organisms/Appbar/hooks';
import useShouldRedirectToWelcomeFlow from 'hooks/useShouldRedirectToWelcomeFlow';
import DevControls from './DevControls';
import { IS_DEVELOPMENT } from 'constants/environment';
import useIsDevelopment from 'hooks/dev/useIsDevelopment';

const AppBarPortal = ({ scrollRef }) => {
  const history = useHistory();
  const queryClient = new QueryClient();
  const [open, setOpen] = useState(false);
  const {
    userProfile: { firstName, lastName },
  } = useContext(UserContext);

  const {
    marketingInfo: { name },
    organizationId,
    propertyId,
    authenticationPortal,
  } = useContext(AppContext);

  const { shouldRedirectToWelcomeFlow } = useShouldRedirectToWelcomeFlow();

  const baseURL = `/${organizationId}/${propertyId}`;

  const isCurrentURL = {
    dashboard: history.location.pathname === `${baseURL}/dashboard`,
    termsAndConditions:
      history.location.pathname === `${baseURL}/terms-and-conditions`,
  };

  const logoutUser = async () => {
    clearUserMetadata();
    if (authenticationPortal) {
      queryClient.clear();
      return logoutFusionAuthUser();
    }
    queryClient.clear();
    destroyLocalAuth();
    return history.push(baseURL);
  };

  const goBackButtonProps = useGoBack({ history });

  const { isDevMode, toggleIsDevMode } = useIsDevelopment();

  return (
    <AppBar
      scrollRef={scrollRef}
      open={open}
      setOpen={setOpen}
      sx={{ paddingTop: 1.5, paddingBottom: 1.5 }}
      toolbarChildren={
        <>
          <IconButton
            aria-label={'go back'}
            data-testid={'backButton'}
            {...goBackButtonProps}
          >
            <ArrowBackIcon htmlColor={'white'} />
          </IconButton>
          <Typography
            variant="titleMedium"
            component="div"
            color={'common.white'}
            align={'center'}
            width={'100%'}
          >
            {name}
          </Typography>
          <IconButton
            aria-label={'open drawer'}
            data-testid={'drawerButton'}
            onClick={() => setOpen(true)}
          >
            <MenuIcon htmlColor={'white'} />
          </IconButton>
        </>
      }
    >
      <List
        sx={{
          position: 'relative',
          paddingX: 2,
          paddingBottom: `${50 + 32}px`, // [logoutButtonHeight + bottom padding]
          height: '100%',
          minHeight: 'fit-content',
          width: 300,
        }}
      >
        <ListItemButton
          sx={{ height: 54, justifyContent: 'flex-end' }}
          onClick={() => setOpen(false)}
        >
          <ListItemIcon>
            <CloseIcon />
          </ListItemIcon>
        </ListItemButton>

        <ListItem sx={{ border: 'none' }}>
          <ListItemAvatar>
            <Avatar>
              {firstName?.charAt(0)}
              {lastName?.charAt(0)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={`${firstName} ${lastName}`}
            primaryTypographyProps={{ color: 'inherit' }}
          />
        </ListItem>

        {shouldRedirectToWelcomeFlow === false && ( // Show when welcome flow is completed
          <>
            <ListItemButton
              LinkComponent={ReactRouterLink}
              to={`${baseURL}/dashboard`}
              selected={isCurrentURL.dashboard}
            >
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary={'Home'} />
            </ListItemButton>

            <ListItemButton
              LinkComponent={ReactRouterLink}
              to={`${baseURL}/terms-and-conditions`}
              selected={isCurrentURL.termsAndConditions}
            >
              <ListItemIcon>
                <ContractIcon />
              </ListItemIcon>
              <ListItemText primary={'Terms and Conditions'} />
            </ListItemButton>
          </>
        )}

        {IS_DEVELOPMENT && isDevMode && <DevControls />}

        {IS_DEVELOPMENT && (
          <ListItemButton
            onClick={toggleIsDevMode}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 2,
              opacity: isDevMode ? 1 : 0.5,
            }}
          >
            <ListItemText
              primary={isDevMode ? 'Disable Dev Mode' : 'Enable Dev Mode'}
              primaryTypographyProps={{
                variant: 'caption',
                align: 'center',
              }}
            />
          </ListItemButton>
        )}

        <ListItemButton
          sx={{
            position: 'absolute',
            bottom: 16,
            width: 'calc(100% - 32px)',
          }}
          onClick={logoutUser}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={'Logout'} />
        </ListItemButton>
      </List>
    </AppBar>
  );
};

export default AppBarPortal;
